import { useCallback, useEffect, useRef, useState } from "react";
import { useTreeRefresh } from "../../contexts/TreeRefreshContext";
import type { CSHFormData } from "../../components/forms/CSHForm";
import {
    createCshGroupGrid, deleteCshGroupGrid, downloadCshGroupSites, downloadCshGroupUsers,
    getCustomer, getGroupDetailsGrid, getUsers, listCshGroupsGrid, updateCshGroupGrid,
} from "../../services/csh/csh.service";
import type { AssignedUserSelection, CshGroup } from "../../services/csh/csh.types";
import {
    buildAssignedEventUsers, buildCshGroupPayload, getAssignedUserLabel,
    getCshErrorMessage, groupSitesByCustomer, syncCshAssignments,
} from "./cshGroupAssignments";
import type { CshCustomerOption } from "./cshGroupAssignments";

async function loadGroups(): Promise<CshGroup[]> {
    const response = await listCshGroupsGrid();
    if (!response.success || !response.data) throw new Error(response.error?.message || "Failed to load CSH groups");
    return Promise.all(response.data.map(async group => {
        const details = await getGroupDetailsGrid(group.id);
        if (!details.success || !details.data) {
            throw new Error(details.error?.message || `Failed to load details for "${group.name}"`);
        }
        return {
            ...group,
            id: String(group.id),
            assignedTo: details.data.users.map(user => ({
                id: String(user.userId ?? user.id ?? ""),
                label: getAssignedUserLabel(user),
            })),
            customers: groupSitesByCustomer(details.data.sites),
        };
    }));
}

export function useCshGroups(canManage: boolean) {
    const [groups, setGroups] = useState<CshGroup[]>([]);
    const [users, setUsers] = useState<AssignedUserSelection[]>([]);
    const [customers, setCustomers] = useState<CshCustomerOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [lookupsReady, setLookupsReady] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const [success, setSuccess] = useState<{ message: string } | null>(null);
    const [downloadingSites, setDownloadingSites] = useState(false);
    const [downloadingUsers, setDownloadingUsers] = useState(false);
    const requestId = useRef(0);
    const { refreshTree, refreshCsh } = useTreeRefresh();

    const refresh = useCallback(async (): Promise<string | null> => {
        const current = ++requestId.current;
        try {
            const next = await loadGroups();
            if (current === requestId.current) setGroups(next);
            return null;
        } catch (error: unknown) {
            return getCshErrorMessage(error, "Failed to load CSH groups");
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            const groupsError = await refresh();
            if (mounted) {
                if (groupsError) setError(groupsError);
                setLoading(false);
            }
        };
        void load();
        return () => { mounted = false; requestId.current += 1; };
    }, [refresh]);

    useEffect(() => {
        setUsers([]);
        setCustomers([]);
        setLookupsReady(false);
        if (!canManage) return;
        let mounted = true;
        const load = async () => {
            try {
                const [usersResponse, customersResponse] = await Promise.all([getUsers(), getCustomer()]);
                if (!usersResponse.success || !usersResponse.data) {
                    throw new Error(usersResponse.error?.message || "Failed to load users");
                }
                if (!customersResponse.success || !customersResponse.data) {
                    throw new Error(customersResponse.error?.message || "Failed to load customers");
                }
                const customerOptions = customersResponse.data.map(customer => {
                    const id = customer.customerId ?? ("id" in customer && typeof customer.id === "number" ? customer.id : undefined);
                    if (id === undefined) throw new Error("Invalid CSH customer option: customer ID is missing");
                    return { ...customer, customerId: id, id };
                });
                if (mounted) {
                    setUsers(usersResponse.data);
                    setCustomers(customerOptions);
                    setLookupsReady(true);
                }
            } catch (error: unknown) {
                if (mounted) setError(getCshErrorMessage(error, "Failed to load CSH group options"));
            }
        };
        void load();
        return () => { mounted = false; };
    }, [canManage]);

    useEffect(() => {
        if (!success) return;
        const timeout = setTimeout(() => setSuccess(null), 5000);
        return () => clearTimeout(timeout);
    }, [success]);

    const save = async (data: CSHFormData, group: CshGroup | null): Promise<boolean> => {
        setSubmitting(true);
        setModalError(null);
        setError(null);
        setSuccess(null);
        let persisted = false;
        try {
            const payload = buildCshGroupPayload(data, customers);
            const response = group ? await updateCshGroupGrid(group.id, payload) : await createCshGroupGrid(payload);
            if (!response.success || !response.data?.id) {
                throw new Error(response.error?.message || `Failed to ${group ? "update" : "create"} CSH group`);
            }
            persisted = true;
            const groupId = String(response.data.id);
            setGroups(previous => group
                ? previous.map(item => item.id === group.id ? { ...item, name: data.name } : item)
                : [...previous, {
                    id: groupId, name: data.name, assignedTo: [], customers: [], activeAlarms: 0,
                    createdAt: "", updatedAt: "",
                }]);
            const failures = await syncCshAssignments(groupId, data, group);
            const refreshError = await refresh();
            if (refreshError) failures.push(refreshError);
            refreshCsh?.();
            if (group && group.name !== data.name) {
                try {
                    await refreshTree();
                } catch (error: unknown) {
                    failures.push(getCshErrorMessage(error, "Tree refresh failed"));
                }
            }
            if (!failures.length) {
                globalThis.dispatchEvent(new CustomEvent("csh-updated", {
                    detail: { groupId, assignedUsers: buildAssignedEventUsers(data, users) },
                }));
                setSuccess({ message: group ? "CSH Group successfully updated" : "CSH Group successfully added" });
            } else {
                setError(`CSH group saved, but some updates failed: ${failures.join("\n")}`);
            }
            return true;
        } catch (error: unknown) {
            const message = getCshErrorMessage(error, "Failed to save CSH group");
            if (persisted) setError(`CSH group saved, but some updates failed: ${message}`);
            else setModalError(message);
            return persisted;
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (groupId: string) => {
        setError(null);
        setSuccess(null);
        try {
            const response = await deleteCshGroupGrid(groupId);
            if (!response.success) throw new Error(response.error?.message || "Failed to delete CSH group");
            setGroups(previous => previous.filter(group => group.id !== groupId));
            const refreshError = await refresh();
            refreshCsh?.();
            globalThis.dispatchEvent(new CustomEvent("csh-updated", { detail: { groupId, assignedUsers: [] } }));
            if (refreshError) setError(`CSH group deleted. ${refreshError}`);
            else setSuccess({ message: "CSH Group deleted" });
        } catch (error: unknown) {
            setError(getCshErrorMessage(error, "Failed to delete CSH group"));
        }
    };

    const download = async (groupId: string, kind: "sites" | "users") => {
        const setDownloading = kind === "sites" ? setDownloadingSites : setDownloadingUsers;
        setDownloading(true);
        setError(null);
        try {
            await (kind === "sites" ? downloadCshGroupSites(groupId) : downloadCshGroupUsers(groupId));
        } catch (error: unknown) {
            setError(getCshErrorMessage(error, `Failed to download ${kind}`));
        } finally {
            setDownloading(false);
        }
    };

    return {
        groups, users, customers, loading, lookupsReady, submitting, error, modalError, success,
        downloadingSites, downloadingUsers, save, remove, download, setError, setModalError, setSuccess,
    };
}
